// ============================================
// SolicitarAccesoModal — popup de solicitud de distribución
// Estética minimal estilo LoginModal (cream, títulos Canela, inputs
// con línea inferior). Reutiliza applyDistributor para enviar.
// ============================================

import React, { useEffect, useState } from 'react';
import FloatingInput from './FloatingInput';
import { useCustomer } from 'context/CustomerContext';
import { applyDistributor } from 'services/medusa/distributor';
import { lockScroll, unlockScroll } from 'utils/scrollLock';
import { devError } from 'utils/devLog';
import './SolicitarAccesoModal.css';

const initialForm = {
  professional_type: '',
  company_name: '',
  rfc: '',
  contact_name: '',
  email: '',
  phone: '',
  city: '',
  estimated_volume: '',
  message: '',
};

export default function SolicitarAccesoModal({ isOpen, onClose }) {
  const { customer, token } = useCustomer() || {};
  const [isMobile, setIsMobile] = useState(false);
  const [closing, setClosing] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockScroll();
    setClosing(false);
    return () => unlockScroll();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !customer) return;
    setForm((f) => ({
      ...f,
      email: f.email || customer.email || '',
      contact_name:
        f.contact_name ||
        (customer.first_name
          ? `${customer.first_name} ${customer.last_name || ''}`.trim()
          : ''),
    }));
  }, [isOpen, customer]);

  const requestClose = () => {
    if (isMobile) {
      setClosing(true);
      setTimeout(() => {
        setClosing(false);
        onClose?.();
      }, 170);
    } else {
      onClose?.();
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.professional_type) {
      setError('Indica si eres médico, cosmetóloga o spa.');
      return;
    }
    if (
      !form.company_name.trim() ||
      !form.contact_name.trim() ||
      !form.email.trim()
    ) {
      setError('Completa establecimiento, contacto y correo.');
      return;
    }

    setSubmitting(true);
    try {
      await applyDistributor(form, token);
      setSent(true);
    } catch (err) {
      devError('Distributor apply error:', err);
      setError(err?.message || 'No pudimos enviar tu solicitud.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAfterSent = () => {
    setSent(false);
    setForm(initialForm);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div
      className="sa-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className={`sa-modal ${isMobile ? 'mobile' : 'desktop'} ${closing ? 'is-closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sa-title"
      >
        <button
          className="sa-modal__close"
          onClick={requestClose}
          aria-label="Cerrar"
          type="button"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {sent ? (
          <div className="sa-modal__sent">
            <h2 id="sa-title" className="sa-modal__title">¡Solicitud enviada!</h2>
            <p className="sa-modal__subtitle">
              Recibimos tu solicitud. Revisamos cada una con calma y te
              avisamos por correo en 1–3 días hábiles.
            </p>
            <button
              type="button"
              className="sa-modal__submit"
              onClick={handleCloseAfterSent}
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <div className="sa-modal__header">
              <h2 id="sa-title" className="sa-modal__title">Solicitar acceso</h2>
              <p className="sa-modal__subtitle">
                Cuéntanos de tu negocio. Revisamos cada solicitud y respondemos
                por correo en 1–3 días hábiles.
              </p>
            </div>

            {error && <div className="sa-modal__error">{error}</div>}

            <form className="sa-modal__form" onSubmit={handleSubmit} noValidate>
              <fieldset className="sa-modal__fieldset">
                <legend className="sa-modal__legend">
                  ¿Cuál es tu perfil profesional? <span className="sa-modal__required">*</span>
                </legend>
                <div className="sa-modal__radios">
                  {[
                    { value: 'medico', label: 'Médico(a)' },
                    { value: 'cosmetologa', label: 'Cosmetóloga' },
                    { value: 'spa', label: 'Spa' },
                  ].map((opt) => (
                    <label key={opt.value} className="sa-modal__radio">
                      <input
                        type="radio"
                        name="professional_type"
                        value={opt.value}
                        checked={form.professional_type === opt.value}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <FloatingInput
                type="text"
                name="company_name"
                label="Nombre del establecimiento"
                value={form.company_name}
                onChange={handleChange}
                required
                disabled={submitting}
              />
              <FloatingInput
                type="text"
                name="contact_name"
                label="Contacto"
                value={form.contact_name}
                onChange={handleChange}
                required
                disabled={submitting}
              />
              <FloatingInput
                type="email"
                name="email"
                label="Correo electrónico"
                value={form.email}
                onChange={handleChange}
                required
                disabled={submitting}
              />
              <FloatingInput
                type="tel"
                name="phone"
                label="Teléfono"
                value={form.phone}
                onChange={handleChange}
                disabled={submitting}
              />
              <FloatingInput
                type="text"
                name="city"
                label="Ciudad"
                value={form.city}
                onChange={handleChange}
                disabled={submitting}
              />
              <FloatingInput
                type="text"
                name="rfc"
                label="RFC (opcional)"
                value={form.rfc}
                onChange={handleChange}
                disabled={submitting}
              />
              <FloatingInput
                type="text"
                name="estimated_volume"
                label="Volumen estimado mensual (opcional)"
                value={form.estimated_volume}
                onChange={handleChange}
                disabled={submitting}
              />

              <div className="sa-modal__textarea-wrap">
                <label className="sa-modal__textarea-label" htmlFor="sa-message">
                  ¿Algo más que debamos saber?
                </label>
                <textarea
                  id="sa-message"
                  name="message"
                  className="sa-modal__textarea"
                  value={form.message}
                  onChange={handleChange}
                  disabled={submitting}
                  rows={3}
                />
              </div>

              <button
                type="submit"
                className="sa-modal__submit"
                disabled={submitting}
              >
                {submitting ? 'Enviando…' : 'Enviar solicitud'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
